import styled, { css } from 'styled-components';

import { ds } from '@/config/colors';
import { media } from '@/utils';
import type { TimeLeftTone } from '@/utils/guestConfirmation';

/* Wspólne style kroku gościa i ekranu „Czeka na zatwierdzenie gościa” (#351). */

export const PickerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SourceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
`;

export const ItemName = styled.span`
  display: block;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ItemMeta = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const GuestCard = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 12px;
`;

export const GuestCardTexts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const GuestCardName = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

export const Tag = styled.span<{ $tone?: 'neutral' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  ${({ $tone, theme }) => ($tone === 'warning'
    ? css`
      color: ${theme.mode === 'dark' ? '#fcd34d' : '#92400e'};
      background: ${theme.mode === 'dark' ? 'rgba(245, 158, 11, 0.18)' : '#fef3c7'};
    `
    : css`
      color: ${theme.colors.text.secondary};
      background: ${theme.mode === 'dark' ? '#374151' : '#f3f4f6'};
    `)}
`;

export const ClearButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f3f4f6')};
  }
`;

export const Hint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* ------------------------------------------------ ekran zatwierdzenia */

export const Screen = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding-bottom: 24px;
`;

export const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Countdown = styled.div<{ $tone: TimeLeftTone }>`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  font-variant-numeric: tabular-nums;
  ${({ $tone, theme }) => {
    if ($tone === 'low') {
      return css`
        color: ${theme.mode === 'dark' ? '#fca5a5' : '#991b1b'};
        background: ${theme.mode === 'dark' ? 'rgba(239, 68, 68, 0.16)' : '#fef2f2'};
      `;
    }
    if ($tone === 'off') {
      return css`
        color: ${theme.colors.text.secondary};
        background: ${theme.mode === 'dark' ? '#1f2937' : '#f3f4f6'};
      `;
    }
    return css`
      color: ${theme.mode === 'dark' ? '#fcd34d' : '#92400e'};
      background: ${theme.mode === 'dark' ? 'rgba(245, 158, 11, 0.16)' : '#fffbeb'};
    `;
  }}
`;

export const CountdownValue = styled.span`
  font-size: 28px;
  font-weight: 500;
  line-height: 1;
`;

export const CountdownLabel = styled.span`
  font-size: 14px;
`;

export const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Option = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 12px;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

export const OptionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const OptionTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const OptionText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const OptionActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  ${media.mobile} {
    flex-direction: column;

    & > * {
      width: 100%;
    }
  }
`;

export const QrRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
`;

/* Biały kafelek pod kodem QR także w ciemnym motywie: aparat potrzebuje kontrastu. */
export const QrFrame = styled.div`
  display: inline-flex;
  padding: 12px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 12px;
`;

export const LinkText = styled.code`
  display: block;
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  /* Spinner ma domyślnie szerokość 100%: przy tekście obok zabierał pół wiersza na 375 px. */
  & > div:first-child {
    flex: 0 0 auto;
    width: auto;
    height: auto;
  }
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease, background 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase};
  border: 1px solid transparent;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : ds.accentInk)};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#111827' : '#ffffff')};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#d1d5db')};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'transparent' : '#ffffff')};
  color: ${({ theme }) => theme.colors.text.primary};

  &:hover:not(:disabled) {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f9fafb')};
  }
`;

export const ReasonCounter = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  text-align: right;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const TimeLeft = styled.span<{ $tone: TimeLeftTone; $block?: boolean }>`
  display: ${({ $block }) => ($block ? 'block' : 'inline')};
  margin-top: ${({ $block }) => ($block ? '4px' : '0')};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone, theme }) => {
    if ($tone === 'low') return theme.mode === 'dark' ? '#fca5a5' : '#b91c1c';
    if ($tone === 'off') return theme.colors.text.secondary;
    return theme.mode === 'dark' ? '#fcd34d' : '#b45309';
  }};
`;

export const Callout = styled.div<{ $tone: 'warning' | 'neutral' }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
  ${({ $tone, theme }) => ($tone === 'warning'
    ? css`
      color: ${theme.mode === 'dark' ? '#fcd34d' : '#92400e'};
      background: ${theme.mode === 'dark' ? 'rgba(245, 158, 11, 0.16)' : '#fffbeb'};
    `
    : css`
      color: ${theme.colors.text.secondary};
      background: ${theme.mode === 'dark' ? '#1f2937' : '#f3f4f6'};
    `)}
`;

export const FieldError = styled.p`
  margin: 4px 0 0 28px;
  font-size: 12px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f87171' : '#dc2626')};
`;
